import { useCallback, useEffect, useMemo, useState } from "react"
import { getNotificationPermission, isWebPushSupported } from "@/config/web-push"
import {
  enableOrderNotifications,
  isOrderNotificationsEnabled,
  type EnableOrderNotificationsFailure,
} from "@/services/order-notifications"
import { useStorefrontBasePath } from "@/utils/storefront-paths"

export type UseOrderNotificationsResult = {
  /** False on browsers without Web Push, on terminal orders, and after a prior block. */
  visible: boolean
  /** True only after the backend confirmed the subscription. */
  enabled: boolean
  /** Hidden once the customer has blocked notifications — we never re-prompt. */
  showAction: boolean
  busy: boolean
  /** Set only after a failed attempt; never on first render. */
  message: string | null
  enable: () => void
  /**
   * Live notification permission. `"default"` on browsers without Web Push, so callers must pair
   * it with {@link UseOrderNotificationsResult.available}.
   */
  permission: NotificationPermission
  /**
   * True when this browser and tenant could do Web Push for this order at all — before any
   * question of permission or an existing subscription.
   */
  available: boolean
  /**
   * True once the passive "is this browser already subscribed?" check has settled. Callers that
   * would otherwise flash an offer at an already-subscribed customer should wait for this.
   */
  resolved: boolean
}

function failureMessage(reason: EnableOrderNotificationsFailure): string {
  switch (reason) {
    case "permission-denied":
      return "Notifications are blocked for this site in your browser settings."
    case "permission-dismissed":
      return "Notifications were not enabled."
    case "unsupported":
    case "not-configured":
      return "Order notifications are unavailable right now."
    default:
      return "Could not turn on order notifications. Please try again."
  }
}

/**
 * Order Details opt-in state for order-status Web Push.
 *
 * Requests nothing on mount: no permission prompt, no service worker registration. The only
 * mount-time work is a passive check of an existing subscription, and that is skipped entirely
 * unless permission is already granted and this browser was previously registered.
 *
 * The same passive check re-runs when the app returns to the foreground, so the UI reflects a
 * permission the customer changed in OS settings while the app was backgrounded. That refresh is
 * read-only — see the resume effect below.
 */
export function useOrderNotifications(input: {
  restaurantId: string
  /** False when the order can no longer produce a v1 notification. */
  eligible: boolean
}): UseOrderNotificationsResult {
  const { eligible } = input
  const restaurantId = input.restaurantId.trim()
  const storefrontBasePath = useStorefrontBasePath()

  const supported = useMemo(() => isWebPushSupported(), [])
  const [permission, setPermission] = useState<NotificationPermission>(() =>
    getNotificationPermission()
  )
  const [enabled, setEnabled] = useState(false)
  const [busy, setBusy] = useState(false)
  const [message, setMessage] = useState<string | null>(null)
  const [resolved, setResolved] = useState(false)

  const blocked = permission === "denied"
  const candidate = supported && eligible && restaurantId.length > 0

  useEffect(() => {
    if (!candidate) {
      setResolved(true)
      return undefined
    }
    let active = true
    void isOrderNotificationsEnabled(restaurantId).then((result) => {
      if (!active) return
      setEnabled(result)
      setResolved(true)
    })
    return () => {
      active = false
    }
  }, [candidate, restaurantId])

  /**
   * Resume refresh: re-read what the browser already knows when the app comes back to the
   * foreground.
   *
   * Notification permission can change entirely outside the app — on iOS, Settings → RasoiX →
   * Allow Notifications OFF then ON drops the permission to "denied" and then resets it to
   * "default", and drops the push subscription with it. Nothing tells the page, and a Home Screen
   * app is usually suspended rather than killed across that round trip, so without this the hook
   * keeps reporting the permission it read at mount and the customer is shown either a stale
   * "enabled" or no way back in at all.
   *
   * Strictly a state refresh. It re-reads the permission and re-runs the existing read-only
   * subscription check — it never prompts for permission, never subscribes, never unsubscribes,
   * and never touches the backend. Recovery stays an explicit customer action: this only makes
   * the existing "Enable order updates" action reachable again.
   *
   * Event-driven and visible-only: no polling, no timers, and the listener is removed on unmount.
   */
  useEffect(() => {
    if (typeof document === "undefined") return undefined

    let active = true

    const handleVisibilityChange = () => {
      if (document.visibilityState !== "visible") return

      setPermission(getNotificationPermission())

      if (!candidate) return

      // Read-only: returns false unless permission is granted AND this browser still holds the
      // subscription we registered, so a revoked grant self-corrects to "not enabled".
      void isOrderNotificationsEnabled(restaurantId).then((result) => {
        if (!active) return
        setEnabled(result)
      })
    }

    document.addEventListener("visibilitychange", handleVisibilityChange)
    return () => {
      active = false
      document.removeEventListener("visibilitychange", handleVisibilityChange)
    }
  }, [candidate, restaurantId])

  const enable = useCallback(() => {
    if (!candidate || busy) return
    setBusy(true)
    setMessage(null)
    void enableOrderNotifications({ restaurantId, storefrontBasePath })
      .then((result) => {
        if (result.ok) {
          setEnabled(true)
          setPermission("granted")
          return
        }
        // Never claim "enabled" — including when only the backend registration failed.
        setEnabled(false)
        setMessage(failureMessage(result.reason))
        if (result.reason === "permission-denied") setPermission("denied")
      })
      .finally(() => setBusy(false))
  }, [busy, candidate, restaurantId, storefrontBasePath])

  return {
    visible: candidate && (!blocked || message !== null || enabled),
    enabled,
    showAction: !enabled && !blocked,
    busy,
    message,
    enable,
    permission,
    available: candidate,
    resolved,
  }
}
